import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { array, bool, func, object, string } from 'prop-types';
import GradeListView from './GradeListView';
import ViewGrade from './ViewGrade';
import StatusError from '../../StatusError/StatusError';
import { SLIDEOUT_STATUS } from './reducers/StructureModelingAnalyticsReducer';
import * as gradeListActions from './actions/GradeListActions';
import { clickGrade } from './actions/ViewGradeActions';
import SlideoutError from 'ps-components/Slideout/Error/SlideoutError';
import './StructureModelingAnalyticsSlideout.scss';
import { showSlideout } from 'ps-components/Slideout/SlideoutReducer';
import { SLIDEOUTS } from '../../Slideout/Slideouts';
import { CONTACT_SUPPORT_MESSAGE } from '../../../lib/constants';

export const basisOptions = [
  {
    value: 'base',
    label: 'Base Salary'
  },
  {
    value: 'tc',
    label: 'Total Cash'
  },
  {
    value: 'ttc',
    label: 'Total Target Cash'
  },
  {
    value: 'td',
    label: 'Total Direct'
  },
  {
    value: 'tgp',
    label: 'Total Guaranteed Pay'
  }
];

export const SMA_BASIS_KEY = 'SMA_basis_key';

export class StructureModelingAnalyticsSlideout extends Component {
  static propTypes = {
    slideout: object,
    smaData: array,
    employeeData: array,
    slideoutStatus: string,
    launchDarklyFeatureFlags: object,
    getGradeList: func,
    structureData: object,
    totals: object,
    hideSlideout: func,
    showSlideout: func,
    gradeListLoading: bool,
    requestError: bool,
    resetErrors: func,
    clickGrade: func,
    csrfPair: object,
    printGradeListPdf: func,
    setBasis: func,
    basis: string,
    udfList: array,
    loadingGrade: bool,
    getMonetaryUDFs: func,
    organizationId: string
  };

  constructor(props) {
    super(props);
    this.state = { isPdf: false };
    this.hideSlideout = this.hideSlideout.bind(this);
  }

  componentDidMount() {
    const { slideout, getGradeList, getMonetaryUDFs, structureData, basis } = this.props;
    if (this.props.match?.params?.modelId) {
      this.setState({ isPdf: true });
      getMonetaryUDFs();
      getGradeList(parseInt(this.props.match.params.modelId), this.props.match?.params?.basis || basis);
    } else if (!structureData.modelId) {
      this.hideSlideout();
    } else if (slideout.open) {
      getMonetaryUDFs();
      getGradeList(parseInt(structureData.modelId), basis);
    }
  }

  componentDidUpdate(prevProps) {
    const { slideout, getGradeList, getMonetaryUDFs, structureData, basis, setBasis, organizationId } = this.props;
    const foundBasis = localStorage.getItem(SMA_BASIS_KEY + '_' + organizationId);
    if (foundBasis && foundBasis !== basis) setBasis(foundBasis);
    if ((slideout?.open && !prevProps.slideout.open) || (prevProps.basis !== basis && slideout?.open)) {
      getMonetaryUDFs();
      getGradeList(parseInt(structureData.modelId), basis);
    }
  }

  hideSlideout() {
    const { hideSlideout } = this.props;
    hideSlideout();
  }

  getSlideoutContext() {
    const {
      slideoutStatus,
      structureData,
      gradeListLoading,
      requestError,
      resetErrors,
      clickGrade,
      csrfPair,
      printGradeListPdf,
      isPrinting,
      preferences,
      setBasis,
      basis,
      udfList,
      loadingGrade,
      organizationId
    } = this.props;

    let slideoutView;
    const errorOverlay = requestError ? (
      <SlideoutError className="sma-data__error" doClose={resetErrors}>
        <h4>Failed to retrieve grade data. Please try again.</h4>
        <div className="sma-data-error__instructions">
          {CONTACT_SUPPORT_MESSAGE}
        </div>
      </SlideoutError>
    ) : null;

    const basisOptionsWithUDFs = basisOptions.concat(udfList);
    const filteredBasis =
      basis === null || !basis || !basisOptionsWithUDFs.find(({ value }) => value === basis) ? 'base' : basis;
    switch (slideoutStatus) {
      case SLIDEOUT_STATUS.LIST:
        slideoutView = (
          <GradeListView
            smaData={this.props.smaData}
            totals={this.props.totals}
            hideSlideout={this.hideSlideout}
            typeOfStructure={structureData.modelType}
            modelName={structureData.modelName}
            modelId={structureData.modelId}
            gradeListLoading={gradeListLoading}
            clickGrade={clickGrade}
            printGradeListPdf={printGradeListPdf}
            isPdf={this.state.isPdf}
            csrfPair={csrfPair}
            isPrinting={isPrinting}
            preferences={preferences}
            basisOptions={basisOptionsWithUDFs}
            setBasis={setBasis}
            basis={filteredBasis}
            loadingGrade={loadingGrade}
            organizationId={organizationId}
          />
        );
        break;
      case SLIDEOUT_STATUS.VIEW:
        slideoutView = (
          <ViewGrade
            smaData={this.props.smaData}
            hideSlideout={this.hideSlideout}
            typeOfStructure={structureData.modelType}
            employeeData={this.props.employeeData}
            preferences={preferences}
            basis={filteredBasis}
            basisOptions={basisOptionsWithUDFs}
          />
        );
        break;
    }
    return (
      <React.Fragment>
        {errorOverlay}
        {slideoutView}
      </React.Fragment>
    );
  }

  render() {
    return <React.Fragment>{this.getSlideoutContext()}</React.Fragment>;
  }
}

const mapStateToProps = ({ structureModeling, session }) => {
  const { structureModelingAnalytics } = structureModeling;
  return {
    slideoutStatus: structureModelingAnalytics.GradeListReducer.slideoutStatus,
    smaData: structureModelingAnalytics.GradeListReducer.gradeInfo,
    totals: structureModelingAnalytics.GradeListReducer.totals,
    structureData: structureModelingAnalytics.GradeListReducer.structureData,
    gradeListLoading: structureModelingAnalytics.GradeListReducer.gradeListLoading,
    loadingGrade: structureModelingAnalytics.ViewGradeReducer.employeeLoading,
    udfList: structureModelingAnalytics.GradeListReducer.udfList,
    basis: structureModelingAnalytics.GradeListReducer.basis,
    csrfPair: session.csrfPair,
    preferences: session.user.preferences,
    organizationId: session.organization.id,
    isPrinting: structureModelingAnalytics.GradeListReducer.isPrinting,
    requestError:
      structureModelingAnalytics.GradeListReducer.requestError ||
      structureModelingAnalytics.ViewGradeReducer.employeeError
  };
};

const actions = {
  ...gradeListActions,
  clickGrade,
  showSlideout
};

export default connect(mapStateToProps, actions)(withRouter(StructureModelingAnalyticsSlideout));
